import React from 'react'

import HelpOutlineIcon from '@mui/icons-material/HelpOutlineOutlined'
import { Button, Grid, Tooltip } from '@mui/material'
import { planLabel } from 'features/customers/constants/planLabels'
import { usePlans } from 'features/customers/hooks/usePlans'
import { customerEditValidator } from 'features/customers/validators/edit'
import { Link } from 'react-router-dom'
import { UrlProvider } from 'urls'

import { FormSelect, FormTextField, FormToggle } from 'components/input'
import { DescriptionListTable } from 'components/table'
import { withForm } from 'decorators/withForm'

type CustomerEditFormProps = {
  id: string
  handleSubmit: (...args: any[]) => any
  onSubmit: (...args: any[]) => any
}

const CustomerEditForm = ({ id, handleSubmit, onSubmit }: CustomerEditFormProps) => {
  const { data: plans } = usePlans()

  const planOptions = (plans || []).map((plan: any) => ({ value: plan.id, label: planLabel(plan.name) }))

  const config = [
    {
      title: 'Name',
      render: () => <FormTextField name="name" />,
    },
    {
      title: 'Website',
      render: () => <FormTextField name="website" />,
    },
    {
      title: 'Partner',
      render: () => <FormToggle name="is_partner" />,
    },
    {
      title: 'Beta',
      render: () => <FormToggle name="is_beta" />,
    },
    {
      title: 'Sales-led (SLG)',
      render: () => <FormToggle name="is_sales_led" />,
    },
    {
      title: 'Plan',
      render: () => <FormSelect allowEmpty={false} name="plan_id" options={planOptions} />,
    },
    {
      title: (
        <span style={{ alignItems: 'center', display: 'flex', gap: 4 }}>
          Tier
          <Tooltip
            arrow
            placement="top"
            title='Determines the base (plan level) limits for leads, contacts, and phone numbers. This value should match the tier level of the subscription in Stripe. Tier 9 is used for legacy "unlimited" customers (500k leads). Valid values: 1-9.'>
            <HelpOutlineIcon fontSize="small" style={{ color: '#757575', cursor: 'pointer' }} />
          </Tooltip>
        </span>
      ),
      render: () => (
        <>
          <FormTextField slotProps={{ htmlInput: { min: 1, max: 9 } }} name="tier" type="number" />
          <span style={{ color: '#f57c00', fontSize: '0.75rem', display: 'block', marginTop: 4 }}>
            Warning: Changing the tier affects base (plan level) limits for leads, contacts, and phone numbers.
          </span>
        </>
      ),
    },
    {
      title: 'Stripe ID',
      render: () => <FormTextField name="stripe_customer_id" />,
    },
    {
      title: 'Legal Name',
      render: () => <FormTextField name="internalData.legal_name" />,
    },
    {
      title: 'Profitwell ID',
      render: () => <FormTextField name="internalData.profitwell_id" />,
    },
    {
      title: 'Panda Doc Link',
      render: () => <FormTextField name="internalData.pandadoc_link" />,
    },
    {
      title: 'Positive Data Impact',
      render: () => <FormToggle name="internalData.positive_data_impact" />,
    },
  ]

  return (
    <form onSubmit={handleSubmit(onSubmit)}>
      <Grid container spacing={2}>
        <Grid
          size={{
            sm: 12,
          }}>
          <DescriptionListTable config={config} />
        </Grid>
        <Grid
          size={{
            sm: 1,
          }}>
          <Link to={UrlProvider.getUrl('customer', { id, tab: '' })}>
            <Button fullWidth variant="contained">
              Cancel
            </Button>
          </Link>
        </Grid>
        <Grid
          size={{
            sm: 1,
          }}>
          <Button color="secondary" fullWidth type="submit" variant="contained">
            Update
          </Button>
        </Grid>
      </Grid>
    </form>
  )
}

export default withForm({ form: 'customerEdit', validate: customerEditValidator })(CustomerEditForm)
